// ════════════════════════════════════════════════════════════════════
// ieIdentity.js — ONE SOUL, EVERY DOOR
// ════════════════════════════════════════════════════════════════════
// IE_IDENTITY_VERSION 1.0 — minted 2026-08-21.
//
// MASTER COPY: gamemaster/engine/ieIdentity.js — vendored byte-identical.
//
// ────────────────────────────────────────────────────────────────────
// ⚠ THE FINDING THAT PRODUCED THIS FILE — read it before "fixing" anything
// ────────────────────────────────────────────────────────────────────
// IE_CANON_PRIME L107 says: "You don't have separate accounts — you
// have one soul that persists across every surface." Every title on
// the terror-tcg project does share one Firebase project, one
// authDomain, and one uid namespace, so the SOUL is genuinely single.
//
// The SESSION is not, and cannot be made so from the client alone.
//
//   Firebase Auth persists in browser storage, and browser storage is
//   scoped to an ORIGIN. terror-card-game.pages.dev and
//   snake-eyes.pages.dev are different origins. A player signed in at
//   one is, to the browser, a stranger at the other — not because the
//   account differs, but because the session cannot cross.
//
// That is the entire reason the `?uid=` handoff was invented, and it
// is why five titles quietly disagree about how much to trust it. It
// also means the handoff can never be a credential: a player who
// hand-edits `?uid=` in the address bar is NOT signed in as that
// player, they merely told the client a number. Every write they
// attempt is refused by rules (auth.uid !== the claimed uid), so the
// symptom is not a breach — it is a screen full of data that silently
// refuses to save. Worse than a breach to diagnose, and it has been
// live in three titles.
//
// ────────────────────────────────────────────────────────────────────
// THE POSTURE — one rule, replacing three contradictory ones
// ────────────────────────────────────────────────────────────────────
//
//   auth.uid is the only identity that may WRITE.        (Law 8)
//   A handoff uid is a GUEST PASS: it may READ, it may be
//   displayed, and it may never be written from.
//
// So an arriving player sees their real name, their real Mark, their
// real friends list and their real balances immediately — no login
// wall between two rooms of the same Empire — and the moment they try
// to do something that costs or earns, they are asked to confirm who
// they are, with their email already filled in. One field, not two,
// and never a wall in front of a door they already walked through.
//
// ────────────────────────────────────────────────────────────────────
// THE PASSPORT — true SSO, built, dormant, one deploy from live
// ────────────────────────────────────────────────────────────────────
// The real fix is a custom token: the origin the player is LEAVING
// asks a Cloud Function to mint a short-lived passport for their own
// authenticated uid; the origin they are ARRIVING at redeems it with
// signInWithCustomToken() and is genuinely, writably signed in. No
// password typed, no second account, no guest mode.
//
// That needs `admin.auth().createCustomToken()`, which needs Cloud
// Functions, which needs the Blaze plan. ie-functions/ is already
// written-and-undeployed for exactly this reason, so this file
// follows the same pattern: the client half is complete and correct
// below, gated behind SSO_ENABLED. The day the function deploys,
// flip one constant and every title has real single sign-on. Nothing
// else in the Empire has to change — travel() already carries the
// passport param when it exists and falls back to the guest pass when
// it does not.
// ════════════════════════════════════════════════════════════════════

import { useState, useEffect, useCallback, useRef } from "react";
import {
  onAuthStateChanged, setPersistence, browserLocalPersistence,
  signInWithEmailAndPassword, createUserWithEmailAndPassword,
  signInWithCustomToken, updateProfile, signOut as fbSignOut,
} from "firebase/auth";
import { ref, get, update, onValue, runTransaction } from "firebase/database";
import { db, auth } from "./firebase";
import { travelTo } from "./ieSocial";

export const IE_IDENTITY_VERSION = "1.0";

// ⟵ THE ONE SWITCH. Flip to true the day issuePassport deploys.
//    See ie-functions/index.js. Leaving it false costs nothing but a
//    typed password at the first border crossing of a session.
export const SSO_ENABLED = false;

export const PASSPORT_TTL_MS = 60000;   // ⟵ DIAL — a passport is single-crossing

// ════════════════════════════════════════════════════════════════════
// THE HANDOFF — read once, strip immediately, never re-read
// ════════════════════════════════════════════════════════════════════
// Stripping matters for more than tidiness: a uid left in the address
// bar gets bookmarked, pasted into chat, and shared into a
// screenshot. Read it into module state on first import, then take it
// out of the URL before the first render paints.
const HANDOFF_SHAPE = /^[A-Za-z0-9]{20,40}$/;

// ⚠ THIS FUNCTION READS AND DOES NOT STRIP. THAT IS DELIBERATE.
//
// The first draft of this file stripped the params on import, which is
// the tidy thing to do and would have broken every title in the
// Empire. Five apps already read `?uid=` themselves — in a useEffect,
// which runs AFTER module import. A strip up here empties the address
// bar before their effect ever looks at it, and every cross-game
// handoff in the Empire silently stops working, with no error anywhere
// to say why.
//
// So: read at import (early enough that nobody can beat us to it),
// strip never. The host app's existing strip still runs and still
// works. A NEW title with no handoff code of its own calls
// stripHandoff() once and gets the tidy behaviour on purpose.
function readHandoffOnce() {
  if (typeof window === "undefined") return {};
  const out = {};
  try {
    const p = new URLSearchParams(window.location.search);
    const rawUid   = p.get("uid");
    const passport = p.get("pp");
    const partyId  = p.get("partyId");
    const from     = p.get("from");

    // Shape guard. Not security — the rules are security — but it keeps
    // a garbage param from ever being used as a database key.
    if (rawUid && HANDOFF_SHAPE.test(rawUid)) out.uid = rawUid;
    if (passport) out.passport = passport;
    if (partyId)  out.partyId  = partyId;
    if (from)     out.from     = from;
  } catch { /* a malformed URL is not worth crashing a boot over */ }
  return out;
}

// For titles that do NOT already strip. Safe to call twice; safe to
// never call. A uid left in the address bar gets bookmarked, pasted
// into chat and screenshotted, so somebody should call it.
export function stripHandoff() {
  if (typeof window === "undefined") return;
  try {
    const p = new URLSearchParams(window.location.search);
    let touched = false;
    ["uid", "pp", "partyId", "from"].forEach(k => {
      if (p.has(k)) { p.delete(k); touched = true; }
    });
    if (!touched) return;
    const q = p.toString();
    window.history.replaceState({}, "",
      window.location.pathname + (q ? `?${q}` : "") + window.location.hash);
  } catch { /* ignore */ }
}

export const HANDOFF = readHandoffOnce();

// ⚠ MUTABLE, and it has to be. Signing out fires onAuthStateChanged(null),
// which lands in the guest branch, which reads the handoff — so on a
// shared machine "sign out" would put the PREVIOUS player's name, email
// and friends list straight back on screen. clearHandoff() is what makes
// signing out actually sign out.
let _handoffUid = HANDOFF.uid || null;
export const getHandoffUid  = () => _handoffUid;
export const clearHandoff   = () => { _handoffUid = null; };
export const handoffPartyId = HANDOFF.partyId || null;

// ════════════════════════════════════════════════════════════════════
// THE PROFILE SEAL
// ════════════════════════════════════════════════════════════════════
// `name` and `displayName` are BOTH live on profiles/{uid} across the
// Empire, written by different titles, read with `?? ||` chains in
// half a dozen files. That is a drift waiting to happen and it has
// already produced "UNCLAIMED" on real players' Marks.
//
//   `name` is the field. `displayName` is kept in lockstep so nothing
//   that reads the old one breaks, and every future reader uses
//   readName() rather than a chain of its own.
export const NAME_MAX = 24;   // ⟵ LAW — rules.json enforces it everywhere
export const clipName = (s) => String(s || "Unclaimed").slice(0, NAME_MAX);

export const readName = (profile, fallback = "Unclaimed") =>
  (profile && (profile.name || profile.displayName)) || fallback;

// ⚠ NEVER set() THE PROFILE ROOT. This is the single most dangerous
// line that could be written in this file, and the first draft wrote it.
//
// THE RACE. IESocialDock mounts its own auth listener beside <App/>,
// and every host app ALREADY has one — Terror's Plaza.js:3356 creates
// the profile with a full set() carrying chocolateCoins, divineCrests,
// collection, storyProgress and starterDeckId; Pierre's App.js:68 does
// the same with gender and companionId. On a brand-new signup BOTH
// listeners fire on the same auth event, both get() and see nothing,
// and both set(). RTDB set() replaces the WHOLE node, so the later
// write wins outright — and the loser's fields are gone. The player
// sees 300/300 in the UI (the host set its own state from its own
// return value) and reloads into onboarding with an empty wallet.
// Silent, permanent, and exactly the wipe-class shape SEAL 1 exists to
// prevent.
//
// A transaction cannot lose that race: whoever arrives second sees the
// first one's node and aborts, keeping every field the first wrote.
export async function ensureProfile(user, extra = {}, { create = false } = {}) {
  if (!user?.uid) return null;
  const r = ref(db, `profiles/${user.uid}`);

  // ⚠ create DEFAULTS TO FALSE, AND THAT IS THE WHOLE POINT.
  //
  // The Dock mounts its own auth listener beside every host app's. If
  // it were allowed to create a profile it would sometimes WIN the race
  // on a fresh signup — and a stub carrying {uid, email, name,
  // createdAt} is enough to make the host's `if (!profile) create()`
  // skip. In Terror that means the new player never receives the
  // 300 ◕ / 300 ◆ pre-alpha purse, their starterDeckId or their
  // storyProgress (Plaza.js:127-166). They arrive at a working screen
  // with an empty soul, and nothing anywhere errors.
  //
  // The transaction below fixed "who overwrites whom". This fixes the
  // subtler question: WHO IS ALLOWED TO BE FIRST. The answer is always
  // the title — it is the only party that knows what a new soul in its
  // world is born owning. The Dock reads, waits, and mounts after.
  if (!create) {
    const snap = await get(r);
    return snap.exists() ? snap.val() : null;
  }

  const name = clipName(
    extra.name || user.displayName || (user.email || "").split("@")[0]
  );

  const res = await runTransaction(r, (cur) => {
    if (cur) return undefined;          // ⟵ somebody got here first. Hands off.
    return {
      uid: user.uid,
      email: user.email || null,
      name,
      displayName: name,
      createdAt: Date.now(),
      ...extra,
      name,                              // extra may not override identity
      displayName: name,
    };
  });

  const prof = res.snapshot.val();

  // Heal the name/displayName split WITHOUT ever inventing one. Both
  // fields are live across the Empire, written by different titles and
  // read through `??` chains in half a dozen files; readName() is the
  // single reader from here on, and this keeps the two in lockstep so
  // the older readers never see "UNCLAIMED" on a real player.
  const n = readName(prof, null);
  if (n && prof && prof.name !== prof.displayName) {
    await update(r, { name: n, displayName: n }).catch(() => {});
  }
  return prof;
}

// ════════════════════════════════════════════════════════════════════
// useIEAuth — the whole identity surface, one hook, every title
// ════════════════════════════════════════════════════════════════════
// phase:
//   "boot"    — deciding; render nothing or a splash
//   "guest"   — a handoff uid is in hand, profile readable, NOT writable
//   "anon"    — nobody at all; show the login
//   "name"    — signed in, but the soul has no name yet
//   "ready"   — signed in, named, writable. The only phase that earns.
//
// `writable` is the flag every earning call site should gate on, and
// it is deliberately NOT the same as "has a uid". Three titles shipped
// treating those as one thing.
export function useIEAuth({ gameId = null, defaults = {}, createProfile = false } = {}) {
  const [user,    setUser]    = useState(null);
  const [profile, setProfile] = useState(null);
  const [phase,   setPhase]   = useState("boot");
  const [error,   setError]   = useState(null);
  const [busy,    setBusy]    = useState(false);
  const [retry,   setRetry]   = useState(0);
  const redeemed = useRef(false);

  const authUid  = user?.uid || null;
  const uid      = authUid || _handoffUid || null;
  const writable = !!authUid;

  // ── boot ─────────────────────────────────────────────────────────
  useEffect(() => {
    let alive = true;
    let off = null;            // ⚠ must live out here — returning the
                               // unsubscribe from inside an async IIFE
                               // returns a Promise to nobody, and the
                               // listener leaks for the page's life.

    (async () => {
      try { await setPersistence(auth, browserLocalPersistence); } catch { /* private mode */ }

      // THE PASSPORT REDEMPTION — first, because succeeding here means
      // the guest branch below never runs.
      if (SSO_ENABLED && HANDOFF.passport && !redeemed.current) {
        redeemed.current = true;
        try { await signInWithCustomToken(auth, HANDOFF.passport); }
        catch { /* expired or forged — fall through to guest/anon */ }
      }

      if (!alive) return;

      off = onAuthStateChanged(auth, async (u) => {
        if (!alive) return;
        if (u) {
          setUser(u);
          // ⚠ "could not read" is NOT "has no name". Swallowing the
          // error and falling through to the name screen shows an
          // established player a claim-name prompt after one flaky
          // request — and claimName then overwrites their real name
          // with whatever they type. Hold at "boot" and try again.
          let prof, failed = false;
          try { prof = await ensureProfile(u, defaults, { create: createProfile }); }
          catch { failed = true; }
          if (!alive) return;
          if (failed) { setPhase("boot"); setTimeout(() => { if (alive) setRetry(n => n + 1); }, 3000); return; }
          setProfile(prof);
          setPhase(readName(prof, "").trim() ? "ready" : "name");
          return;
        }
        setUser(null);
        // THE GUEST PASS. Read their soul so the room is furnished,
        // and remember that nothing here may be written.
        if (_handoffUid) {
          const snap = await get(ref(db, `profiles/${_handoffUid}`)).catch(() => null);
          if (!alive) return;
          const prof = snap && snap.val ? snap.val() : null;
          setProfile(prof);
          setPhase(prof ? "guest" : "anon");
          return;
        }
        setProfile(null);
        setPhase("anon");
      });

      if (!alive && off) off();
    })();

    return () => { alive = false; if (off) off(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retry]);

  // ── live profile once truly signed in ────────────────────────────
  // A LISTENER, not a one-shot get. With create:false the Dock may
  // legitimately find no profile yet — the host is still writing it —
  // and a one-shot read would leave the social layer hidden until the
  // next reload. This flips it to "ready" the moment the title finishes
  // minting the soul, and keeps the name live if it changes anywhere.
  useEffect(() => {
    if (!authUid) return;
    return onValue(ref(db, `profiles/${authUid}`), snap => {
      const prof = snap.val();
      if (!prof) return;
      setProfile(prof);
      setPhase(ph => (ph === "boot" || ph === "name" || ph === "ready")
        ? (readName(prof, "").trim() ? "ready" : "name")
        : ph);
    }, () => {});
  }, [authUid]);

  // ── the doors ────────────────────────────────────────────────────
  const signIn = useCallback(async (email, password) => {
    setError(null); setBusy(true);
    try {
      await signInWithEmailAndPassword(auth, (email || "").trim(), password);
      setBusy(false);
      return { ok: true };
    } catch (e) {
      setBusy(false);
      const msg = AUTH_ERRORS[e?.code] || "Could not sign in.";
      setError(msg);
      return { ok: false, error: msg };
    }
  }, []);

  const signUp = useCallback(async (email, password, name) => {
    setError(null); setBusy(true);
    try {
      const clean = clipName((name || "").trim() || (email || "").split("@")[0]);
      const cred = await createUserWithEmailAndPassword(auth, (email || "").trim(), password);
      await updateProfile(cred.user, { displayName: clean }).catch(() => {});
      await ensureProfile(cred.user, { ...defaults, name: clean }, { create: true });
      setBusy(false);
      return { ok: true };
    } catch (e) {
      setBusy(false);
      const msg = AUTH_ERRORS[e?.code] || "Could not create that account.";
      setError(msg);
      return { ok: false, error: msg };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const claimName = useCallback(async (name) => {
    const clean = clipName((name || "").trim());
    if (!clean || !authUid) return { ok: false, error: "Pick a name first." };
    await update(ref(db, `profiles/${authUid}`), { name: clean, displayName: clean });
    if (auth.currentUser) await updateProfile(auth.currentUser, { displayName: clean }).catch(() => {});
    setProfile(p => ({ ...(p || {}), name: clean, displayName: clean }));
    setPhase("ready");
    return { ok: true };
  }, [authUid]);

  const signOut = useCallback(async () => {
    clearHandoff();          // ⟵ before fbSignOut, or the guest branch re-adopts them
    try { await fbSignOut(auth); } catch { /* already gone */ }
    setUser(null); setProfile(null); setPhase("anon");
  }, []);

  // ── crossing a border ────────────────────────────────────────────
  // The ONE function every portal, invite and friend-join should call.
  // Mints a passport when SSO is live, hands over the guest pass when
  // it is not, and either way keeps the party together.
  const travel = useCallback(async (url, opts = {}) => {
    if (!url) return false;
    let passport = null;
    if (SSO_ENABLED && authUid) passport = await issuePassport().catch(() => null);
    try {
      const u = new URL(url, window.location.href);
      if (uid)             u.searchParams.set("uid", uid);
      if (passport)        u.searchParams.set("pp", passport);
      if (opts.partyId)    u.searchParams.set("partyId", opts.partyId);
      if (gameId)          u.searchParams.set("from", gameId);
      window.location.href = u.toString();
      return true;
    } catch {
      return travelTo(url, { uid, partyId: opts.partyId, from: gameId });
    }
  }, [uid, authUid, gameId]);

  return {
    user, uid, authUid, profile, phase, writable,
    name: readName(profile),
    email: user?.email || profile?.email || null,
    // A guest arriving from another title — used to pre-fill the login
    // with their own email so signing in is one field, not two.
    guestEmail: (!authUid && profile?.email) || null,
    isGuest: phase === "guest",
    busy, error, setError,
    signIn, signUp, signOut, claimName, travel,
    handoffPartyId,
  };
}

// ════════════════════════════════════════════════════════════════════
// issuePassport — client half of the dormant SSO
// ════════════════════════════════════════════════════════════════════
// Deliberately imports firebase/functions LAZILY. With SSO_ENABLED
// false this line never executes, so no title pays a bundle cost for a
// feature that is not on yet, and no app is forced to add the
// dependency before it is needed.
export async function issuePassport() {
  if (!SSO_ENABLED) return null;
  const { getFunctions, httpsCallable } = await import("firebase/functions");
  const call = httpsCallable(getFunctions(), "issuePassport");
  const res = await call({});
  return res?.data?.token || null;
}

const AUTH_ERRORS = {
  "auth/invalid-email":         "That email doesn't look right.",
  "auth/user-not-found":        "No soul by that name. Create one?",
  "auth/wrong-password":        "Wrong password.",
  "auth/invalid-credential":    "Email or password is wrong.",
  "auth/email-already-in-use":  "That email already carries a soul. Sign in instead.",
  "auth/weak-password":         "Six characters minimum.",
  "auth/too-many-requests":     "Too many tries. Wait a moment.",
  "auth/network-request-failed":"The Empire is unreachable right now.",
};

export default useIEAuth;
